import {
  Component,
  OnInit,
  AfterViewInit,
  Injectable,
  inject,
  Injector,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { provideRouter, withRouterConfig } from '@angular/router';
import { filter, take, withLatestFrom } from 'rxjs/operators';

import { TemplateService } from './services/template/template.service';
import { ComponentRegistryService } from './services/component-registry/component-registry.service';
import { registerAppComponents } from './utils/register-app-components';
import { Store } from '@ngrx/store';
import { AppState } from './ngrx/template/template.reducers';
import { TokenSyncService } from './services/token-sync/token-sync.service';
import { SplashScreenComponent } from './components/splash-screen/splash-screen.component';
import { SplashScreenCoordinatorService } from './services/splash-screen/splash-screen-coordinator.service';
import { UpdateNotificationComponent } from './components/update-notification/update-notification.component';
import { OfflineIndicatorComponent } from './components/offline-indicator/offline-indicator.component';
import { ConnectivityService } from './services/connectivity/connectivity.service';
import { ThemeService } from './services/theme/theme.service';
import { SiteCacheService } from './services/site-cache/site-cache.service';
import { setInjector } from '../shared/db-utils';
import * as AuthActions from './ngrx/auth/auth.actions';
import {
  selectCurrentPortal,
  selectIsAuthInitialized,
} from './ngrx/auth/auth.selectors';
import { ComponentDataSourceService } from './services/component-data-source/component-data-source.service';
import { environment } from './environment/environment';
import { ComponentDataFilterService } from './services/component-data-filter/component-data-filter.service';
import { registerEventsFilter } from './services/component-data-filter/data/events-filter';
import { Routes } from '@angular/router';
import { RouterOutlet } from '@angular/router';
import { dynamicTableQuestion } from './surveyJS-custom-questions/dynamic-table-question';
import { AuthService } from './services/auth/auth.service';
import { debounceTime } from 'rxjs/operators';
import * as TemplateActions from './ngrx/template/template.actions';
import { GoogleAnalyticsService } from './services/google-analytics/google-analytics.service';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class AppInitializer {
  private initialized = false;

  initialize(callback: () => void) {
    if (!this.initialized) {
      callback();
      this.initialized = true;
    }
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [
    RouterOutlet,
    SplashScreenComponent,
    UpdateNotificationComponent,
    OfflineIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AppInitializer],
})
export class AppComponent implements OnInit, AfterViewInit {
  // Enhanced preloading strategies
  private adminPortalSlugs = [
    'dashboard',
    'ressourcer',
    'lokationer',
    'brugere',
    'events',
    'indstillinger',
  ];
  private userPortalSlugs = [
    'profile',
    'events',
    'indstillinger',
    'mine-kurser',
  ];

  private injector = inject(Injector);
  private router = inject(Router);
  private templateService = inject(TemplateService);
  private registryService = inject(ComponentRegistryService);
  private appInitializer = inject(AppInitializer);
  private store = inject(Store<AppState>);
  private tokenSyncService = inject(TokenSyncService);
  private splashScreenCoordinator = inject(SplashScreenCoordinatorService);
  private themeService = inject(ThemeService);
  private siteCacheService = inject(SiteCacheService);
  private componentDataSourceService = inject(ComponentDataSourceService);
  private componentDataFilterService = inject(ComponentDataFilterService);
  private titleService = inject(Title);
  private initializationComplete = false;
  private authService = inject(AuthService);
  private connectivityService = inject(ConnectivityService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);

  constructor() {
    setInjector(this.injector);

    if (Capacitor.isNativePlatform()) {
      document.documentElement.classList.add('duna-native');
      document.body.classList.add('duna-native');
    }

    // Set the document title based on debug flag
    // debug = true → "DUNA TEST", debug = false → "DUNA"
    const title = environment.debug ? 'DUNA TEST' : 'DUNA';
    this.titleService.setTitle(title);

    // Update PWA manifest title dynamically if running as installed PWA
    this.updatePwaManifestTitle();
  }

  async ngOnInit() {
    try {
      registerAppComponents(this.registryService);
      this.splashScreenCoordinator.initialize();

      void this.tokenSyncService.initialize();

      // Google Analytics service is automatically initialized and tracks page views

      this.setupNavigationHandling();

      this.registerFilters();

      this.appInitializer.initialize(() => {
        this.setupNavigationHandling();
        const appRoot = document.querySelector('app-root');
        if (appRoot) {
          appRoot.removeAttribute('inert');
        }
      });

      this.themeService.applyTheme();

      // Enhanced preloading strategy
      this.setupEnhancedPreloading();

      // Make the component data source service available globally for SurveyJS widgets
      (window as any).componentDataSourceService =
        this.componentDataSourceService;

      // The widget self-registers through its import, but we log it to confirm
    } catch (error) {
      console.error('[App] Error during initialization:', error);
    }
  }

  ngAfterViewInit() {
    // View initialization complete
  }

  private setupEnhancedPreloading(): void {

    this.store
      .select(selectIsAuthInitialized)
      .pipe(
        filter((initialized) => initialized),
        take(1),
        withLatestFrom(this.store.select(selectCurrentPortal))
      )
      .subscribe(([_, portal]) => {
        if (portal) {
          this.preloadPortalTemplates(portal);
        } else {
        }
      });

    // Set up connectivity-based preloading
    this.connectivityService
      .getConnectivityStatus()
      .pipe(
        filter((isOnline) => isOnline),
        debounceTime(2000), // Wait for stable connection
        take(1)
      )
      .subscribe(() => {
        this.performBackgroundPreloading();
      });
  }

  private preloadPortalTemplates(portal: string): void {
    const slugsToPreload =
      portal === 'administration' ? this.adminPortalSlugs : this.userPortalSlugs;

    // Defer background preload so the current page can render from cache first.
    setTimeout(() => {
      this.templateService.preloadPortalSlugs(portal).subscribe({
        next: () => {},
        error: (error) => {
          console.error(`[App] Error during ${portal} portal preload:`, error);
          this.fallbackIndividualPreload(slugsToPreload);
        },
      });
    }, 3000);
  }

  private performBackgroundPreloading(): void {
    // Background preloading disabled - using existing portal-based preloading instead
  }

  private fallbackIndividualPreload(slugs: string[]): void {

    // Preload templates one by one with delays to avoid overwhelming the server
    slugs.forEach((slug, index) => {
      setTimeout(() => {
        this.store.dispatch(TemplateActions.loadPageDataBySlug({ slug }));
      }, index * 500); // 500ms delay between each request
    });
  }

  private setupNavigationHandling() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Page view tracking is handled automatically by GoogleAnalyticsService
      }
    });
  }

  /**
   * Updates the PWA manifest title dynamically based on environment
   * This is a client-side approach to complement the build-time solution
   */
  private updatePwaManifestTitle() {
    if ('serviceWorker' in navigator) {
      // Only attempt to modify if we're in a PWA context
      if (
        window.matchMedia('(display-mode: standalone)').matches ||
        (navigator as any).standalone === true
      ) {
        // Set title based on debug flag: debug = true → "DUNA TEST", debug = false → "DUNA"
        const title = environment.debug ? 'DUNA TEST' : 'DUNA';

        // Update the meta tags for Apple devices
        const metaTags = document.querySelectorAll(
          'meta[name="apple-mobile-web-app-title"]'
        );
        if (metaTags.length > 0) {
          metaTags.forEach((tag) => {
            (tag as HTMLMetaElement).content = title;
          });
        } else {
          // Create the meta tag if it doesn't exist
          const metaTag = document.createElement('meta');
          metaTag.name = 'apple-mobile-web-app-title';
          metaTag.content = title;
          document.head.appendChild(metaTag);
        }

      }
    }
  }

  /**
   * Register all filter functions with the filter service
   */
  registerFilters() {
    try {
      // Register the events filter
      registerEventsFilter(this.componentDataFilterService);
    } catch (error) {
      console.error('[App] Error registering filters:', error);
    }
  }
}
